import {
  PUZZLE_TICK_RATE,
  type PuzzleEvent,
  type PuzzleBotDifficulty,
  type PuzzleInput,
  type PuzzleMatchState,
  type PuzzleMode
} from "../simulation";
import type { PuzzleLobbyParticipant, PuzzleLobbyState } from "../protocol";
import { PuzzleInputController } from "../input/PuzzleInputController";
import { LocalPuzzleDriver } from "../network/LocalPuzzleDriver";
import {
  PUZZLE_ROOM_PRESETS,
  isLocalDevelopmentHostname,
  type CreatedPuzzleRoom,
  type PuzzleRoomDraft,
  type PuzzleRoomPreview,
  type PuzzleRoomPreset,
} from "../network/PuzzleLobbyApi";
import { PuzzleRenderer } from "../phaser/createPuzzleRenderer";
import {
  FOAM_MUSIC_DANGER_ENTER_ROWS,
  FoamMusicDirector,
  puzzleRowsBeforeLaunchLine,
  type FoamMusicState
} from "../audio/FoamMusicDirector";
import { FOAM_POP_MUSIC_TRACKS } from "../audio/manifest";
import { puzzleResultPresentation, type PuzzleResultPresentation } from "./resultPresentation";
import { bindRoomViewport } from "./roomViewport";
import {
  normalizePuzzleBubbleKind,
  puzzleColorStyle,
  puzzleBubbleKindLabel,
  summarizePuzzleEvent,
  toPuzzleViewModel,
  type PuzzleBubbleKindView,
  type PuzzlePlayerView,
  type PuzzleTeamView,
  type PuzzleViewModel
} from "../phaser/viewModel";

export type PuzzleConnectionState =
  | "local"
  | "connecting"
  | "live"
  | "reconnecting"
  | "offline"
  | "error";

export interface PuzzleFoamAppOptions {
  arenaHref?: string;
  onInviteRequested?: () => void | Promise<void>;
  onRoomCreatorRequested?: () => void;
  onRoomCreateRequested?: (draft: PuzzleRoomDraft) => Promise<CreatedPuzzleRoom>;
}

export interface PuzzleRemoteMatchOptions {
  playerId: string;
  title?: string;
  onInput: (input: PuzzleInput) => void;
  onReady: () => void;
  onExit?: () => void;
}

export interface PuzzleRoomAdmissionOptions {
  onJoin: (nickname: string, team: "blue" | "orange" | null) => Promise<void>;
  onExit?: () => void;
}

/**
 * DOM application shell for Foam Pop. Local practice owns a local driver;
 * invited sessions inject snapshots/events and provide only an input sink.
 */
export class PuzzleFoamApp {
  private readonly input = new PuzzleInputController();
  private readonly music = new FoamMusicDirector();
  private readonly unsubscribeMusic: () => void;
  private readonly options: PuzzleFoamAppOptions;
  private renderer: PuzzleRenderer | null = null;
  private localDriver: LocalPuzzleDriver | null = null;
  private remoteOptions: PuzzleRemoteMatchOptions | null = null;
  private remoteInputTimer: number | null = null;
  private latestState: PuzzleMatchState | null = null;
  private latestLobby: PuzzleLobbyState | null = null;
  private latestView: PuzzleViewModel | null = null;
  private playerId = "p1";
  private lastPhase = "";
  private lastRound = 0;
  private noticeTimer: number | null = null;
  private toastTimer: number | null = null;
  private toastActive = false;
  private toastQueue: Array<{ label: string; tone: string }> = [];
  private localMode: PuzzleMode | null = null;
  private activePortrait = false;
  private resizeFrame: number | null = null;
  private holdFeedbackTimer: number | null = null;
  private roomOverlay: HTMLElement | null = null;
  private releaseRoomViewport: (() => void) | null = null;
  private roomPreviousFocus: HTMLElement | null = null;
  private destroyed = false;
  private remoteConnectionState: PuzzleConnectionState = "connecting";
  private remoteConnectionLabel = "CONNECTING";

  private readonly handleRoomKeydown = (event: KeyboardEvent): void => {
    const overlay = this.roomOverlay;
    if (!overlay) return;
    if (event.key === "Escape") {
      this.closeRoomCreator();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...overlay.querySelectorAll<HTMLElement>(
      "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex='-1'])",
    )].filter((element) => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  private readonly handleMusicGesture = (event: Event): void => {
    if (this.destroyed) return;
    if (event instanceof KeyboardEvent && ["Tab", "Shift", "Alt", "Meta", "Control"].includes(event.key)) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest("#fp-music-toggle")) return;
    if (target?.closest("[data-puzzle-launch]")) this.music.setCue("gameplay");
    const state = this.music.state();
    if (!state.unlocked && !state.muted) this.music.unlockFromGesture();
  };

  private readonly handleResize = (): void => {
    if (!this.renderer || this.destroyed) return;
    if (this.resizeFrame !== null) window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null;
      const portrait = this.shouldUsePortraitLayout();
      if (portrait !== this.activePortrait) this.applyRenderLayout(portrait);
    });
  };

  private readonly handleVisibilityChange = (): void => {
    const hidden = document.hidden;
    this.renderer?.setPaused(hidden);
    this.music.setSuspended(hidden);
    if (hidden) {
      this.localDriver?.stop();
      this.stopRemoteInputLoop();
    } else if (this.renderer) {
      this.localDriver?.start();
      this.startRemoteInputLoop();
    }
  };

  constructor(private readonly root: HTMLElement, options: PuzzleFoamAppOptions = {}) {
    this.options = options;
    this.unsubscribeMusic = this.music.subscribe((state) => this.syncMusicControl(state));
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("resize", this.handleResize);
    this.root.addEventListener("pointerdown", this.handleMusicGesture, { passive: true });
    this.root.addEventListener("keydown", this.handleMusicGesture);
  }

  mount(): void {
    this.assertAlive();
    this.showLanding();
  }

  showLanding(): void {
    this.assertAlive();
    this.cleanupActiveMatch();
    this.music.setCue("intro");
    const arenaHref = this.options.arenaHref ?? "/arena";
    const sessionQuery = new URLSearchParams(location.search);
    const hasInvite = Boolean(
      this.options.onInviteRequested
      && (sessionQuery.get("invite") || sessionQuery.get("token") || sessionQuery.get("match"))
    );
    this.root.className = "foam-pop foam-pop--landing";
    this.root.innerHTML = `
      <main class="fp-landing-shell">
        <section class="fp-brand-stage" aria-labelledby="foam-pop-title">
          <div class="fp-brand-constellation" aria-hidden="true">
            <span class="fp-brand-orb fp-brand-orb--cyan" data-shape="triangle"></span>
            <span class="fp-brand-orb fp-brand-orb--coral" data-shape="diamond"></span>
            <span class="fp-brand-orb fp-brand-orb--lime" data-shape="ring"></span>
            <i></i><i></i><i></i>
          </div>
          <div class="fp-yaong-keyart" aria-hidden="true"><span></span></div>
          <p class="fp-eyebrow">FOAM LAB · COMPETITIVE TEST 01</p>
          <h1 id="foam-pop-title">FOAM <em>POP</em></h1>
          <p class="fp-brand-copy">같은 색 3개를 연결하고, 지지대를 잃은 구슬을 상대에게 보내세요.<br />판을 비워도 경기는 계속됩니다. 구슬 더미가 아래 출발선에 닿는 팀이 패배합니다.</p>
          <div class="fp-system-strip" aria-label="게임 특징">
            <span><i class="fp-signal fp-signal--live"></i>30 HZ SIM</span>
            <span>BEST OF 3</span>
            <span>TOUCH READY</span>
          </div>
        </section>

        <section class="fp-launch-panel" aria-label="Foam Pop 시작 메뉴">
          <header class="fp-launch-heading">
            <span>01</span>
            <div><p class="fp-eyebrow">SELECT LAB RUN</p><h2>경기 시작</h2></div>
          </header>

          ${hasInvite ? `
            <button class="fp-launch-card fp-launch-card--invite" type="button" data-puzzle-launch="invite">
              <span class="fp-launch-card__mode">LINK</span>
              <span class="fp-launch-card__copy"><strong>초대 경기 접속</strong><small>지정된 보드와 실시간 연결</small></span>
              <span class="fp-launch-card__arrow" aria-hidden="true">→</span>
            </button>
          ` : ""}

          ${this.options.onRoomCreateRequested ? `
            <button class="fp-launch-card fp-launch-card--multiplayer" type="button" data-puzzle-launch="multiplayer">
              <span class="fp-launch-card__mode">LIVE</span>
              <span class="fp-launch-card__copy"><strong>멀티플레이 방 만들기</strong><small>1:1 · 2:2 + AI · 4인 토너먼트</small></span>
              <span class="fp-launch-card__arrow" aria-hidden="true">↗</span>
            </button>
          ` : ""}

          <button class="fp-launch-card" type="button" data-puzzle-launch="duel">
            <span class="fp-launch-card__mode">1:1</span>
            <span class="fp-launch-card__copy"><strong>솔로 랩 듀얼</strong><small>NOVA vs KITE · 로컬 AI</small></span>
            <span class="fp-launch-card__arrow" aria-hidden="true">→</span>
          </button>

          <button class="fp-launch-card fp-launch-card--team" type="button" data-puzzle-launch="team">
            <span class="fp-launch-card__mode">2:2</span>
            <span class="fp-launch-card__copy"><strong>팀 압력 실험</strong><small>YOU + AI vs AI + AI</small></span>
            <span class="fp-launch-card__arrow" aria-hidden="true">→</span>
          </button>

          <fieldset class="fp-difficulty-picker">
            <legend><span>AI DIFFICULTY</span><strong>중 · 실수 25%</strong></legend>
            <div class="fp-difficulty-options">
              ${this.difficultyOption("easy", "하", "실수 45%")}
              ${this.difficultyOption("medium", "중", "실수 25%", true)}
              ${this.difficultyOption("hard", "상", "실수 10%")}
              ${this.difficultyOption("superhuman", "초인", "실수 3%")}
              ${this.difficultyOption("god", "신", "실수 0%")}
            </div>
          </fieldset>

          <label class="fp-fullscreen-toggle">
            <input id="fp-fullscreen-start" type="checkbox" />
            <span class="fp-toggle-track" aria-hidden="true"><i></i></span>
            <span><strong>전체 화면으로 시작</strong><small>지원되는 브라우저에서 선택 적용</small></span>
          </label>

          <div class="fp-landing-links">
            <button id="fp-music-toggle" class="fp-music-toggle" type="button" aria-pressed="false">
              <span aria-hidden="true">♪</span>
              <b data-music-label>음악 시작</b>
              <small data-music-track>INTRO · CC0</small>
            </button>
            <a class="fp-arena-link" href="${this.escapeAttribute(arenaHref)}">기존 Foam Arena 열기 <span aria-hidden="true">↗</span></a>
            <p><kbd>←</kbd><kbd>→</kbd> 조준 · <kbd>SPACE</kbd> 발사 · <kbd>C</kbd>/<kbd>SHIFT</kbd> 홀드</p>
          </div>
          <p class="fp-launch-error" role="alert" aria-live="polite"></p>
        </section>
      </main>
      ${this.orientationGuard()}
    `;

    this.bindMusicControl();

    this.root.querySelectorAll<HTMLButtonElement>("[data-puzzle-launch]").forEach((button) => {
      button.addEventListener("click", () => {
        const mode = button.dataset.puzzleLaunch;
        if (mode === "multiplayer") {
          if (this.options.onRoomCreatorRequested) this.options.onRoomCreatorRequested();
          else this.showRoomCreator();
          return;
        }
        void this.prepareStart().then(async () => {
          if (mode === "invite") {
            try {
              await this.options.onInviteRequested?.();
            } catch (cause) {
              this.music.setCue("intro");
              this.setText(".fp-launch-error", cause instanceof Error ? cause.message : "초대 경기에 연결할 수 없습니다.");
            }
          } else {
            this.startLocalMatch(mode === "team" ? "team" : "duel", this.selectedDifficulty());
          }
        });
      });
    });
  }

  showRoomCreator(): void {
    const createRoom = this.options.onRoomCreateRequested;
    if (!createRoom || this.roomOverlay) return;
    const localDevelopment = isLocalDevelopmentHostname(location.hostname);
    const selectedDifficulty = this.selectedDifficulty();
    this.roomPreviousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const overlay = document.createElement("dialog");
    overlay.className = "fp-room-overlay";
    overlay.setAttribute("aria-labelledby", "fp-room-title");
    overlay.innerHTML = `
      <section class="fp-room-dialog" tabindex="-1">
        <header class="fp-room-header">
          <div>
            <p class="fp-eyebrow">PRIVATE ONLINE ROOM</p>
            <h2 id="fp-room-title">멀티플레이 방 만들기</h2>
          </div>
          <button class="fp-room-close" type="button" data-room-close aria-label="멀티플레이 방 만들기 닫기">×</button>
        </header>

        <form id="fp-room-form" class="fp-room-form">
          <div class="fp-room-fields">
          <fieldset class="fp-room-presets">
            <legend>경기 형식</legend>
            ${this.roomPresetOption("duel", true)}
            ${this.roomPresetOption("team-ai")}
            ${this.roomPresetOption("team")}
            ${this.roomPresetOption("tournament")}
          </fieldset>

          <div class="fp-room-settings">
            <label>
              <span>방 이름</span>
              <input name="roomName" type="text" maxlength="80" value="YAONG FOAM ROOM" autocomplete="off" required />
            </label>
            <label>
              <span>AI 난이도</span>
              <select name="botDifficulty">
                ${this.roomDifficultyOptions(selectedDifficulty)}
              </select>
              <small>AI가 있는 형식과 접속 종료 후 대체 AI에 적용됩니다.</small>
            </label>
            <label class="fp-room-host-key">
              <span>방장 키 · 관리자 전용</span>
              <input name="hostKey" type="password" maxlength="512" autocomplete="off"${localDevelopment ? "" : " required"} />
              <small>${localDevelopment
                ? "로컬 개발 서버에서는 키 없이 테스트할 수 있습니다."
                : "운영 관리자만 입력하세요. 브라우저에 저장하거나 초대 링크에 넣지 않습니다."}</small>
            </label>
          </div>

          <section class="fp-room-capacity" aria-labelledby="fp-room-capacity-title">
            <div class="fp-room-capacity__icon" aria-hidden="true">URL</div>
            <div>
              <small id="fp-room-capacity-title">공용 참가 주소</small>
              <strong id="fp-room-capacity-count">사람 2명</strong>
              <span id="fp-room-format-note">${PUZZLE_ROOM_PRESETS.duel.note}</span>
            </div>
            <ol id="fp-room-capacity-steps">
              <li>방장이 주소 하나를 공유</li>
              <li>참가자가 닉네임과 A/B팀 선택</li>
              <li>모두 준비하면 동시 시작</li>
            </ol>
          </section>

          <div class="fp-room-security-note">
            <strong>공용 링크 = 방 입장권</strong>
            <span>주소를 아는 사람은 빈 좌석을 선점할 수 있습니다. 정해진 참가자에게만 전달하세요.</span>
          </div>
          </div>
          <p id="fp-room-status" class="fp-room-status" role="alert" aria-live="assertive"></p>
          <footer class="fp-room-actions">
            <button type="button" data-room-close>취소</button>
            <button class="is-primary" type="submit">방 만들고 공용 URL 발급</button>
          </footer>
        </form>
      </section>
    `;
    this.roomOverlay = overlay;
    this.root.classList.add("foam-pop--room-open");
    this.root.querySelector<HTMLElement>(".fp-landing-shell")?.setAttribute("inert", "");
    this.root.append(overlay);
    this.releaseRoomViewport = bindRoomViewport(overlay);
    // The browser top layer escapes the menu's clipping/scrolling ancestors.
    overlay.showModal();
    overlay.addEventListener("cancel", (event) => {
      event.preventDefault();
      this.closeRoomCreator();
    });
    window.addEventListener("keydown", this.handleRoomKeydown);

    overlay.querySelectorAll<HTMLButtonElement>("[data-room-close]").forEach((button) => {
      button.addEventListener("click", () => this.closeRoomCreator());
    });
    overlay.addEventListener("pointerdown", (event) => {
      if (event.target === overlay) this.closeRoomCreator();
    });
    overlay.querySelectorAll<HTMLInputElement>("input[name='roomPreset']").forEach((input) => {
      input.addEventListener("change", () => this.refreshRoomCapacity(overlay));
    });
    this.refreshRoomCapacity(overlay);
    this.requiredWithin<HTMLFormElement>(overlay, "#fp-room-form").addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submitRoomCreator(overlay, createRoom);
    });
    requestAnimationFrame(() => {
      if (this.roomOverlay === overlay) {
        // Opening the dialog must not summon the mobile keyboard or scroll its parent.
        overlay.querySelector<HTMLElement>(".fp-room-dialog")?.focus({ preventScroll: true });
      }
    });
  }

  private closeRoomCreator(): void {
    if (!this.roomOverlay) return;
    this.releaseRoomViewport?.();
    this.releaseRoomViewport = null;
    window.removeEventListener("keydown", this.handleRoomKeydown);
    this.roomOverlay.remove();
    this.roomOverlay = null;
    this.root.classList.remove("foam-pop--room-open");
    this.root.querySelector<HTMLElement>(".fp-landing-shell")?.removeAttribute("inert");
    const focus = this.roomPreviousFocus;
    this.roomPreviousFocus = null;
    if (focus?.isConnected) focus.focus({ preventScroll: true });
  }

  private refreshRoomCapacity(overlay: HTMLElement): void {
    const preset = this.selectedRoomPreset(overlay);
    this.setTextWithin(overlay, "#fp-room-format-note", PUZZLE_ROOM_PRESETS[preset].note);
    this.setTextWithin(
      overlay,
      "#fp-room-capacity-count",
      `사람 ${PUZZLE_ROOM_PRESETS[preset].humanCount}명`,
    );
    const steps = overlay.querySelector<HTMLOListElement>("#fp-room-capacity-steps");
    if (steps) {
      steps.children[1]!.textContent = preset === "tournament"
        ? "참가자가 닉네임 입력 · 대진 자동 배정"
        : "참가자가 닉네임과 A/B팀 선택";
    }
  }

  private async submitRoomCreator(
    overlay: HTMLElement,
    createRoom: (draft: PuzzleRoomDraft) => Promise<CreatedPuzzleRoom>,
  ): Promise<void> {
    const form = this.requiredWithin<HTMLFormElement>(overlay, "#fp-room-form");
    const submit = this.requiredWithin<HTMLButtonElement>(form, "button[type='submit']");
    const status = this.requiredWithin<HTMLElement>(form, "#fp-room-status");
    submit.disabled = true;
    submit.textContent = "공용 방 여는 중…";
    form.setAttribute("aria-busy", "true");
    status.textContent = "";
    try {
      const draft = this.readRoomDraft(form);
      const room = await createRoom(draft);
      const hostKey = form.elements.namedItem("hostKey");
      if (hostKey instanceof HTMLInputElement) hostKey.value = "";
      if (this.roomOverlay !== overlay) return;
      this.renderRoomInvitations(overlay, room);
    } catch (error) {
      if (this.roomOverlay !== overlay) return;
      status.textContent = error instanceof Error ? error.message : "방을 만들 수 없습니다.";
      submit.disabled = false;
      submit.textContent = "방 만들고 공용 URL 발급";
      form.removeAttribute("aria-busy");
    }
  }

  private readRoomDraft(form: HTMLFormElement): PuzzleRoomDraft {
    const preset = this.selectedRoomPreset(form);
    const difficulty = this.formValue(form, "botDifficulty");
    const botDifficulty: PuzzleBotDifficulty = difficulty === "easy"
      || difficulty === "hard"
      || difficulty === "superhuman"
      || difficulty === "god"
      ? difficulty
      : "medium";
    return {
      preset,
      name: this.formValue(form, "roomName"),
      botDifficulty,
      hostKey: this.formValue(form, "hostKey"),
    };
  }

  private renderRoomInvitations(overlay: HTMLElement, room: CreatedPuzzleRoom): void {
    const dialog = this.requiredWithin<HTMLElement>(overlay, ".fp-room-dialog");
    const matchSummary = room.matchIds.length > 1 ? `${room.matchIds.length}개 준결승 방` : "실시간 경기 1개";
    dialog.innerHTML = `
      <header class="fp-room-header">
        <div><p class="fp-eyebrow">ROOM READY · ONE SHARED URL</p><h2 id="fp-room-title">공용 방 주소가 준비됐습니다</h2></div>
        <button class="fp-room-close" type="button" data-room-close aria-label="공용 방 주소 닫기">×</button>
      </header>
      <div class="fp-room-success">
        <div class="fp-room-ready-mark" aria-hidden="true">✓</div>
        <div class="fp-room-ready-copy">
          <strong>${this.escapeHtml(room.name)}</strong>
          <span>${this.escapeHtml(PUZZLE_ROOM_PRESETS[room.preset].title)} · 사람 ${room.humanCapacity}명 · ${matchSummary}</span>
        </div>
        <p>아래 주소 하나를 참가자 모두에게 전달하세요. 접속한 사람이 닉네임과 팀을 직접 고릅니다.</p>
        <label class="fp-room-shared-link">
          <span>공용 참가 URL</span>
          <input type="text" readonly value="${this.escapeAttribute(room.joinUrl)}" aria-label="공용 참가 URL" />
        </label>
        <p id="fp-room-status" class="fp-room-status" role="status" aria-live="polite"></p>
        <footer class="fp-room-actions">
          <button type="button" data-copy-room>주소 복사</button>
          <button type="button" data-share-room>공유</button>
          <a class="fp-room-action-link" href="${this.escapeAttribute(room.joinUrl)}">이 기기에서 참가</a>
          <button class="is-primary" type="button" data-room-close>완료</button>
        </footer>
        <small class="fp-room-expiry">주소는 24시간 동안 유효하며 정원이 차면 추가 입장이 닫힙니다. 참가 세션은 같은 기기에서 8시간 동안 다시 연결할 수 있습니다.</small>
      </div>
    `;

    dialog.querySelectorAll<HTMLButtonElement>("[data-room-close]").forEach((button) => {
      button.addEventListener("click", () => this.closeRoomCreator());
    });
    this.requiredWithin<HTMLButtonElement>(dialog, "[data-copy-room]").addEventListener("click", () => {
      void this.copyRoomText(room.joinUrl, overlay, "공용 참가 주소를 복사했습니다.");
    });
    this.requiredWithin<HTMLButtonElement>(dialog, "[data-share-room]").addEventListener("click", () => {
      void this.shareRoomLink(room, overlay);
    });
    requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
  }

  private async shareRoomLink(
    room: CreatedPuzzleRoom,
    overlay: HTMLElement,
  ): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} · Foam Pop`,
          text: `Foam Pop 방에 참가해 닉네임과 A/B팀을 선택하세요.`,
          url: room.joinUrl,
        });
        this.setTextWithin(overlay, "#fp-room-status", "공용 참가 주소 공유를 열었습니다.");
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    await this.copyRoomText(room.joinUrl, overlay, "공용 참가 주소를 복사했습니다.");
  }

  private async copyRoomText(value: string, overlay: HTMLElement, confirmation: string): Promise<void> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement("textarea");
        input.value = value;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.append(input);
        input.select();
        const copied = document.execCommand("copy");
        input.remove();
        if (!copied) throw new Error("copy unavailable");
      }
      this.setTextWithin(overlay, "#fp-room-status", confirmation);
    } catch {
      this.setTextWithin(overlay, "#fp-room-status", "자동 복사가 막혔습니다. 참가 링크를 길게 눌러 복사해 주세요.");
    }
  }

  private roomPresetOption(preset: PuzzleRoomPreset, checked = false): string {
    const spec = PUZZLE_ROOM_PRESETS[preset];
    const badge = preset === "duel" ? "1:1" : preset === "team-ai" ? "2+2" : preset === "team" ? "2:2" : "CUP";
    return `
      <label>
        <input type="radio" name="roomPreset" value="${preset}"${checked ? " checked" : ""} />
        <span><b>${badge}</b><strong>${spec.title}</strong><small>${spec.note}</small></span>
      </label>
    `;
  }

  private roomDifficultyOptions(selected: PuzzleBotDifficulty): string {
    const entries: Array<[PuzzleBotDifficulty, string]> = [
      ["easy", "하 · 실수 45%"],
      ["medium", "중 · 실수 25%"],
      ["hard", "상 · 실수 10%"],
      ["superhuman", "초인 · 실수 3%"],
      ["god", "신 · 실수 0%"],
    ];
    return entries.map(([value, label]) => `<option value="${value}"${value === selected ? " selected" : ""}>${label}</option>`).join("");
  }

  private selectedRoomPreset(parent: ParentNode): PuzzleRoomPreset {
    const value = parent.querySelector<HTMLInputElement>("input[name='roomPreset']:checked")?.value;
    return value === "team-ai" || value === "team" || value === "tournament" ? value : "duel";
  }

  private teamPartyLabel(team: "blue" | "orange"): string {
    return team === "blue" ? "A팀 · CYAN" : "B팀 · CORAL";
  }

  private formValue(form: HTMLFormElement, name: string): string {
    const field = form.elements.namedItem(name);
    return field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : "";
  }

  showRoomAdmission(preview: PuzzleRoomPreview, options: PuzzleRoomAdmissionOptions): void {
    this.assertAlive();
    this.cleanupActiveMatch();
    this.music.setCue("intro");
    const blueNames = preview.participants.filter((participant) => participant.team === "blue").map((participant) => participant.name);
    const orangeNames = preview.participants.filter((participant) => participant.team === "orange").map((participant) => participant.name);
    const defaultTeam = blueNames.length <= orangeNames.length ? "blue" : "orange";
    const closed = preview.full || preview.started;
    const roster = (names: string[]): string => {
      const entries = Array.from({ length: preview.teamCapacity }, (_, index) => names[index] ?? "빈 자리");
      return entries.map((name) => `
        <li class="${name === "빈 자리" ? "is-open" : ""}">
          <span aria-hidden="true">${name === "빈 자리" ? "+" : this.escapeHtml(name.slice(0, 1).toUpperCase())}</span>
          <strong>${this.escapeHtml(name)}</strong>
        </li>
      `).join("");
    };
    this.root.className = "foam-pop foam-pop--lobby foam-pop--room-admission";
    this.root.innerHTML = `
      <main class="fp-room-admission" aria-labelledby="fp-admission-title">
        <header class="fp-room-admission__header">
          <div class="fp-live-lobby__brand" aria-hidden="true"><span>FP</span><i></i></div>
          <div>
            <p class="fp-eyebrow">SHARED MULTIPLAYER ROOM</p>
            <h1 id="fp-admission-title">${this.escapeHtml(preview.name)}</h1>
            <p>${this.escapeHtml(PUZZLE_ROOM_PRESETS[preview.preset].title)} · ${preview.joinedHumans}/${preview.humanCapacity}명 입장</p>
          </div>
          <button id="fp-admission-exit" class="fp-room-close" type="button" aria-label="방에서 나가기">×</button>
        </header>

        <section class="fp-room-admission__intro">
          <div><small>01</small><strong>닉네임 입력</strong><span>게임에서 표시할 이름</span></div>
          <div><small>02</small><strong>${preview.allowTeamChoice ? "A/B팀 선택" : "대진 자동 배정"}</strong><span>${preview.allowTeamChoice ? "빈 자리가 있는 팀" : "서버가 4강 위치 결정"}</span></div>
          <div><small>03</small><strong>로비 준비</strong><span>전원이 모이면 READY</span></div>
        </section>

        <section class="fp-room-admission__body">
          <div class="fp-room-admission__teams" aria-label="현재 A/B팀 참가자">
            <article class="fp-admission-team fp-admission-team--blue">
              <header><span>A</span><div><small>CYAN PARTY</small><strong>A팀</strong></div><b>${blueNames.length}/${preview.teamCapacity}</b></header>
              <ul>${roster(blueNames)}</ul>
            </article>
            <div class="fp-lobby-versus" aria-hidden="true"><span>VS</span><i></i></div>
            <article class="fp-admission-team fp-admission-team--orange">
              <header><span>B</span><div><small>CORAL PARTY</small><strong>B팀</strong></div><b>${orangeNames.length}/${preview.teamCapacity}</b></header>
              <ul>${roster(orangeNames)}</ul>
            </article>
          </div>

          <form id="fp-room-admission-form" class="fp-room-admission__form">
            <div class="fp-room-admission__form-heading">
              <small>YOUR PLAYER CARD</small>
              <h2>참가 정보 정하기</h2>
              <p>${closed
                ? preview.started ? "이미 경기가 시작된 방입니다." : "방 정원이 모두 찼습니다."
                : "닉네임과 팀은 입장 후 서버가 확정합니다."}</p>
            </div>
            <label class="fp-admission-nickname">
              <span>닉네임</span>
              <input name="nickname" type="text" minlength="1" maxlength="24" placeholder="예: YAONG" autocomplete="nickname" required${closed ? " disabled" : ""} />
            </label>
            ${preview.allowTeamChoice ? `
              <fieldset class="fp-admission-team-picker">
                <legend>참가할 팀</legend>
                <button type="button" data-admission-team="blue" aria-pressed="${defaultTeam === "blue"}"${blueNames.length >= preview.teamCapacity ? " disabled" : ""}>
                  <span>A</span><strong>A팀 · CYAN</strong><small>${blueNames.length}/${preview.teamCapacity}명</small>
                </button>
                <button type="button" data-admission-team="orange" aria-pressed="${defaultTeam === "orange"}"${orangeNames.length >= preview.teamCapacity ? " disabled" : ""}>
                  <span>B</span><strong>B팀 · CORAL</strong><small>${orangeNames.length}/${preview.teamCapacity}명</small>
                </button>
                <input name="team" type="hidden" value="${defaultTeam}" />
              </fieldset>
            ` : `
              <div class="fp-admission-auto-team"><span>SEED</span><div><strong>대진 위치 자동 배정</strong><small>입장 순서에 따라 서버가 준결승 좌석을 확정합니다.</small></div></div>
            `}
            <p id="fp-admission-status" class="fp-room-status" role="alert" aria-live="assertive"></p>
            <button class="fp-admission-submit" type="submit"${closed ? " disabled" : ""}>닉네임으로 로비 입장</button>
            <small class="fp-room-expiry">공용 주소를 아는 사람만 입장할 수 있습니다. 한 기기에서는 한 좌석만 사용하세요.</small>
          </form>
        </section>
      </main>
    `;
    this.required<HTMLButtonElement>("#fp-admission-exit").addEventListener("click", () => options.onExit?.());
    this.root.querySelectorAll<HTMLButtonElement>("[data-admission-team]").forEach((button) => {
      button.addEventListener("click", () => {
        const team = button.dataset.admissionTeam === "orange" ? "orange" : "blue";
        const input = this.required<HTMLInputElement>("input[name='team']");
        input.value = team;
        this.root.querySelectorAll<HTMLButtonElement>("[data-admission-team]").forEach((candidate) => {
          candidate.setAttribute("aria-pressed", String(candidate === button));
        });
      });
    });
    this.required<HTMLFormElement>("#fp-room-admission-form").addEventListener("submit", (event) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      const submit = this.requiredWithin<HTMLButtonElement>(form, "button[type='submit']");
      const nickname = this.formValue(form, "nickname");
      const team = preview.allowTeamChoice
        ? this.formValue(form, "team") === "orange" ? "orange" as const : "blue" as const
        : null;
      submit.disabled = true;
      submit.textContent = "서버에서 좌석 확인 중…";
      form.setAttribute("aria-busy", "true");
      this.setTextWithin(form, "#fp-admission-status", "");
      void options.onJoin(nickname, team).catch((error) => {
        if (!submit.isConnected) return;
        submit.disabled = false;
        submit.textContent = "닉네임으로 로비 입장";
        form.removeAttribute("aria-busy");
        this.setTextWithin(
          form,
          "#fp-admission-status",
          error instanceof Error ? error.message : "방에 입장할 수 없습니다.",
        );
      });
    });
    requestAnimationFrame(() => this.root.querySelector<HTMLInputElement>("input[name='nickname']")?.focus());
  }

  startLocalMatch(mode: PuzzleMode, difficulty: PuzzleBotDifficulty = "medium"): void {
    this.assertAlive();
    this.cleanupActiveMatch();
    this.music.setCue("gameplay");
    this.playerId = "p1";
    this.localMode = mode;
    this.activePortrait = this.shouldUsePortraitLayout();
    const duelComparison = mode === "duel" && !this.activePortrait;
    this.renderMatchShell(
      mode === "team" ? `2:2 AI SCRIMMAGE · ${this.difficultyLabel(difficulty)}` : `1:1 AI DUEL · ${this.difficultyLabel(difficulty)}`,
      "local",
      this.activePortrait,
      duelComparison
    );
    this.attachGameAdapters(this.playerId, this.activePortrait, duelComparison);
    this.localDriver = new LocalPuzzleDriver({
      mode,
      botDifficulty: difficulty,
      readHumanInput: () => this.input.read(),
      onSnapshot: (state) => this.publish(state),
      onEvents: (events) => this.publishEvents(events)
    });
    this.localDriver.start();
    const reset = this.required<HTMLButtonElement>("#fp-reset-match");
    reset.hidden = false;
    reset.addEventListener("click", () => this.localDriver?.reset());
  }

  /**
   * Opens the game shell for a socket-backed session. The caller owns the
   * socket and calls publish/publishEvents/setConnection from its callbacks.
   */
  startRemoteMatch(options: PuzzleRemoteMatchOptions): void {
    this.assertAlive();
    this.cleanupActiveMatch();
    this.music.setCue("intro");
    this.playerId = options.playerId;
    this.remoteOptions = options;
    this.remoteConnectionState = "connecting";
    this.remoteConnectionLabel = "CONNECTING";
    this.renderRemoteLobbyShell(options.title ?? "PRIVATE FOAM POP ROOM");
  }

  publish(state: PuzzleMatchState): void {
    if (this.destroyed) return;
    this.latestState = state;
    if (!this.renderer) return;
    this.renderer?.publish(state);
    const view = toPuzzleViewModel(state, this.playerId);
    this.latestView = view;
    this.updateHud(view);
    this.music.updateForMatch(view);
  }

  publishLobby(lobby: PuzzleLobbyState): void {
    if (this.destroyed || !this.remoteOptions) return;
    this.latestLobby = lobby;
    if (lobby.started) {
      this.enterRemoteGameplay();
      return;
    }
    if (!this.root.classList.contains("foam-pop--lobby")) {
      this.input.setEnabled(false);
      this.renderer?.destroy();
      this.renderer = null;
      this.stopRemoteInputLoop();
      this.latestView = null;
      this.lastPhase = "";
      this.lastRound = 0;
      this.music.setCue("intro");
      this.renderRemoteLobbyShell(this.remoteOptions.title ?? "PRIVATE FOAM POP ROOM");
      this.setConnection(this.remoteConnectionState, this.remoteConnectionLabel);
    }
    this.updateRemoteLobby(lobby);
  }

  publishEvents(events: PuzzleEvent[]): void {
    if (this.destroyed || events.length === 0) return;
    this.renderer?.publishEvents(events);
    for (const event of events) {
      const summary = summarizePuzzleEvent(event);
      if (!summary) continue;
      const raw = event as unknown as Record<string, unknown>;
      const type = typeof raw.type === "string" ? raw.type : "";
      const count = Array.isArray(raw.cells) ? raw.cells.length : 0;
      const rows = typeof raw.rows === "number" ? Math.max(0, raw.rows) : 0;
      const bubbles = typeof raw.bubbles === "number" ? Math.max(0, raw.bubbles) : 0;
      const combo = typeof raw.combo === "number" ? Math.max(0, raw.combo) : 0;
      const previousCombo = typeof raw.previousCombo === "number" ? Math.max(0, raw.previousCombo) : 0;
      const bonusRows = typeof raw.bonusRows === "number" ? Math.max(0, raw.bonusRows) : 0;
      const kind = normalizePuzzleBubbleKind(raw.kind ?? raw.bubbleKind);
      const load = `${rows > 0 ? `${rows}R` : ""}${rows > 0 && bubbles > 0 ? " · " : ""}${bubbles > 0 ? `${bubbles}B` : ""}`;
      const label = type === "matched" ? `FOAM POP${count ? ` ×${count}` : ""}`
        : type === "dropped" ? `CLUSTER DROP${count ? ` ×${count}` : ""}`
        : type === "bubble_held" ? "HOLD SWAP"
        : type === "special_triggered" ? `${puzzleBubbleKindLabel(kind)} BURST${count ? ` ×${count}` : ""}`
        : type === "combo_changed" || type === "combo_updated" ? `COMBO ×${combo}${bonusRows > 0 ? ` · +${bonusRows}R` : ""}`
        : type === "combo_broken" ? `COMBO LOST${previousCombo > 1 ? ` · ×${previousCombo}` : ""}`
        : type === "pressure_queued" ? `ATTACK SENT${load ? ` · ${load}` : ""}`
        : type === "pressure_applied" ? `PRESSURE IN${load ? ` · ${load}` : ""}`
        : summary.label;
      this.queueEventToast(label, summary.tone);
      if (type === "bubble_held") this.showHoldFeedback();
    }
  }

  setConnection(state: PuzzleConnectionState, label?: string): void {
    const resolvedLabel = label ?? (state === "local" ? "LOCAL SIM" : state.toUpperCase());
    this.remoteConnectionState = state;
    this.remoteConnectionLabel = resolvedLabel;
    this.root.querySelectorAll<HTMLElement>("#fp-connection, #fp-lobby-connection").forEach((chip) => {
      chip.dataset.state = state;
      const text = chip.querySelector("span");
      if (text) text.textContent = resolvedLabel;
    });
    if (this.latestLobby && !this.latestLobby.started) this.updateRemoteLobby(this.latestLobby);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.cleanupActiveMatch();
    this.input.dispose();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("resize", this.handleResize);
    this.root.removeEventListener("pointerdown", this.handleMusicGesture);
    this.root.removeEventListener("keydown", this.handleMusicGesture);
    this.unsubscribeMusic();
    this.music.destroy();
    this.root.replaceChildren();
    this.root.className = "";
    this.destroyed = true;
  }

  private renderRemoteLobbyShell(title: string): void {
    this.root.className = "foam-pop foam-pop--lobby";
    this.root.innerHTML = `
      <main class="fp-live-lobby" aria-labelledby="fp-live-lobby-title">
        <header class="fp-live-lobby__header">
          <div class="fp-live-lobby__brand" aria-hidden="true"><span>FP</span><i></i></div>
          <div>
            <p class="fp-eyebrow">PRIVATE MULTIPLAYER LOBBY</p>
            <h1 id="fp-live-lobby-title">${this.escapeHtml(title)}</h1>
            <p id="fp-live-lobby-room">참가자 정보를 불러오는 중…</p>
          </div>
          <div id="fp-lobby-connection" class="fp-connection" data-state="connecting"><i class="fp-signal"></i><span>CONNECTING</span></div>
        </header>

        <section class="fp-live-lobby__brief" aria-label="로비 안내">
          <div><small>MATCH FORMAT</small><strong id="fp-lobby-mode">A/B TEAM</strong></div>
          <div><small>PARTY SIZE</small><strong id="fp-lobby-capacity">2 PLAYERS</strong></div>
          <div><small>READY CHECK</small><strong id="fp-lobby-ready-count">0 / 0</strong></div>
          <p>닉네임과 팀 편성을 확인하세요. 모든 참가자가 준비를 완료하면 동시에 경기가 시작됩니다.</p>
        </section>

        <section class="fp-lobby-parties" aria-label="A팀과 B팀 참가자">
          <article class="fp-lobby-party fp-lobby-party--blue">
            <header><span>A</span><div><small>CYAN PARTY</small><strong>A팀</strong></div><b id="fp-lobby-blue-count">0 / 2</b></header>
            <div id="fp-lobby-blue-roster" class="fp-lobby-party__roster" aria-live="polite"></div>
          </article>
          <div class="fp-lobby-versus" aria-hidden="true"><span>VS</span><i></i></div>
          <article class="fp-lobby-party fp-lobby-party--orange">
            <header><span>B</span><div><small>CORAL PARTY</small><strong>B팀</strong></div><b id="fp-lobby-orange-count">0 / 2</b></header>
            <div id="fp-lobby-orange-roster" class="fp-lobby-party__roster" aria-live="polite"></div>
          </article>
        </section>

        <footer class="fp-live-lobby__footer">
          <div class="fp-lobby-self-status">
            <small>YOUR SEAT</small>
            <strong id="fp-lobby-self-team">좌석 확인 중</strong>
            <span id="fp-lobby-status" role="status" aria-live="polite">서버에 연결하고 있습니다.</span>
          </div>
          <div class="fp-live-lobby__actions">
            <button id="fp-music-toggle" class="fp-utility-button fp-music-toggle fp-music-toggle--compact" type="button" aria-pressed="false">
              <span aria-hidden="true">♪</span><b data-music-label>음악 시작</b><small data-music-track>INTRO · CC0</small>
            </button>
            <button id="fp-lobby-exit" class="fp-lobby-secondary" type="button">로비 나가기</button>
            <button id="fp-lobby-ready" class="fp-lobby-ready" type="button" disabled><span>READY</span><strong>준비 완료</strong></button>
          </div>
        </footer>
      </main>
    `;
    this.bindMusicControl();
    this.required<HTMLButtonElement>("#fp-lobby-ready").addEventListener("click", () => {
      const button = this.required<HTMLButtonElement>("#fp-lobby-ready");
      if (!this.remoteOptions || this.remoteConnectionState !== "live") return;
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      button.querySelector("strong")!.textContent = "준비 전송 중";
      this.setText("#fp-lobby-status", "준비 상태를 서버에 전송했습니다.");
      try {
        this.remoteOptions.onReady();
      } catch {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.querySelector("strong")!.textContent = "준비 완료";
        this.setText("#fp-lobby-status", "준비 상태를 전송하지 못했습니다. 다시 시도해 주세요.");
      }
    });
    this.required<HTMLButtonElement>("#fp-lobby-exit").addEventListener("click", () => {
      const onExit = this.remoteOptions?.onExit;
      this.showLanding();
      onExit?.();
    });
  }

  private updateRemoteLobby(lobby: PuzzleLobbyState): void {
    if (!this.root.classList.contains("foam-pop--lobby")) return;
    const capacity = lobby.teamCapacity;
    const blue = lobby.participants.filter((participant) => participant.team === "blue");
    const orange = lobby.participants.filter((participant) => participant.team === "orange");
    const humans = lobby.participants.filter((participant) => participant.kind === "human");
    const readyHumans = humans.filter((participant) => participant.ready && participant.connected).length;
    const self = lobby.participants.find((participant) => participant.id === this.playerId);
    this.setText("#fp-live-lobby-room", lobby.roomName);
    this.setText("#fp-lobby-mode", lobby.mode === "team" ? "2 : 2 TEAM MATCH" : "1 : 1 DUEL");
    this.setText("#fp-lobby-capacity", `${capacity} PLAYER${capacity === 1 ? "" : "S"} / TEAM`);
    this.setText("#fp-lobby-ready-count", `${readyHumans} / ${humans.length}`);
    this.setText("#fp-lobby-blue-count", `${blue.filter((participant) => participant.kind !== "open").length} / ${capacity}`);
    this.setText("#fp-lobby-orange-count", `${orange.filter((participant) => participant.kind !== "open").length} / ${capacity}`);
    this.replaceLobbyRoster("#fp-lobby-blue-roster", blue, capacity, "blue");
    this.replaceLobbyRoster("#fp-lobby-orange-roster", orange, capacity, "orange");
    this.setText(
      "#fp-lobby-self-team",
      self ? `${self.team === "blue" ? "A팀 · CYAN" : "B팀 · CORAL"} · ${self.name}` : "좌석 확인 중",
    );

    const ready = this.root.querySelector<HTMLButtonElement>("#fp-lobby-ready");
    if (ready) {
      const canReady = Boolean(self && self.kind === "human" && self.connected && !self.ready && this.remoteConnectionState === "live");
      ready.disabled = !canReady;
      ready.removeAttribute("aria-busy");
      const label = ready.querySelector("strong");
      if (label) label.textContent = self?.ready ? "준비 완료됨" : "준비 완료";
      ready.dataset.ready = String(self?.ready === true);
    }
    const waiting = humans.filter((participant) => !participant.ready || !participant.connected).map((participant) => participant.name);
    this.setText(
      "#fp-lobby-status",
      self?.ready
        ? waiting.length > 0 ? `${waiting.join(", ")} 참가자의 준비를 기다리는 중입니다.` : "모든 참가자가 준비했습니다. 경기를 시작합니다."
        : this.remoteConnectionState === "live" ? "팀과 닉네임을 확인한 뒤 준비 완료를 눌러주세요." : "실시간 서버에 다시 연결하고 있습니다.",
    );
  }

  private replaceLobbyRoster(
    selector: string,
    participants: PuzzleLobbyParticipant[],
    capacity: number,
    team: PuzzleTeamView,
  ): void {
    const roster = this.root.querySelector<HTMLElement>(selector);
    if (!roster) return;
    const cards = participants.map((participant) => {
      const card = document.createElement("article");
      const isOpen = participant.kind === "open";
      card.className = `fp-lobby-player${participant.id === this.playerId ? " is-you" : ""}${isOpen ? " is-empty" : participant.connected ? "" : " is-offline"}`;
      card.dataset.ready = String(participant.ready);
      const avatar = document.createElement("span");
      avatar.className = "fp-lobby-player__avatar";
      avatar.textContent = isOpen ? "+" : participant.kind === "bot" ? "AI" : participant.name.trim().slice(0, 1).toUpperCase();
      const identity = document.createElement("div");
      const name = document.createElement("strong");
      name.textContent = isOpen ? "빈 자리" : `${participant.name}${participant.id === this.playerId ? " · 나" : ""}`;
      const meta = document.createElement("small");
      meta.textContent = isOpen
        ? "공용 URL 참가 대기"
        : participant.kind === "bot"
        ? `${participant.botDifficulty ? this.difficultyLabel(participant.botDifficulty) : "AI"} · 준비됨`
        : participant.connected ? participant.ready ? "접속됨 · 준비 완료" : "접속됨 · 준비 전" : "초대 접속 대기";
      identity.append(name, meta);
      const state = document.createElement("b");
      state.textContent = isOpen ? "OPEN" : !participant.connected ? "WAIT" : participant.ready ? "READY" : "ONLINE";
      card.append(avatar, identity, state);
      return card;
    });
    for (let index = participants.length; index < capacity; index += 1) {
      const empty = document.createElement("article");
      empty.className = "fp-lobby-player is-empty";
      const marker = document.createElement("span");
      marker.className = "fp-lobby-player__avatar";
      marker.textContent = "+";
      const identity = document.createElement("div");
      const name = document.createElement("strong");
      name.textContent = "EMPTY SLOT";
      const meta = document.createElement("small");
      meta.textContent = `${team === "blue" ? "A" : "B"}팀 좌석`;
      identity.append(name, meta);
      empty.append(marker, identity);
      cards.push(empty);
    }
    roster.replaceChildren(...cards);
  }

  private enterRemoteGameplay(): void {
    const options = this.remoteOptions;
    if (!options || this.renderer) return;
    this.music.setCue("gameplay");
    const portrait = this.shouldUsePortraitLayout();
    this.activePortrait = portrait;
    this.renderMatchShell(options.title ?? "PRIVATE FOAM POP MATCH", this.remoteConnectionState, portrait, false);
    this.setConnection(this.remoteConnectionState, this.remoteConnectionLabel);
    this.attachGameAdapters(options.playerId, portrait);
    this.startRemoteInputLoop();
    if (this.latestState) this.publish(this.latestState);
  }

  private renderMatchShell(
    title: string,
    connection: PuzzleConnectionState,
    portrait: boolean,
    duelComparison = false
  ): void {
    this.root.className = `foam-pop foam-pop--game${portrait ? " foam-pop--portrait" : ""}${duelComparison ? " foam-pop--duel-compare" : ""}`;
    this.root.innerHTML = `
      <main class="fp-game-shell">
        <header class="fp-match-hud" aria-label="Foam Pop 경기 상태">
          <section class="fp-team fp-team--blue">
            <div class="fp-team__score" data-perspective="self" aria-label="내 점수, 시안 팀 0점"><span id="fp-score-blue-role" class="fp-team__score-role">내 점수</span><strong id="fp-score-blue">0</strong></div>
            <div class="fp-team__meta">
              <span><i aria-hidden="true">△</i> CYAN TEAM</span><small id="fp-wins-blue">0 ROUND</small>
              <div id="fp-roster-blue" class="fp-roster"></div>
            </div>
          </section>

          <section class="fp-match-core">
            <div class="fp-match-clock">
              <p id="fp-match-mode" class="fp-visually-hidden">${this.escapeHtml(title)}</p>
              <strong id="fp-round-time">90</strong>
              <span id="fp-round-label">ROUND 1 · BEST OF 3</span>
            </div>
            <div class="fp-orb-queue" aria-label="발사 오브 순서">
              <span id="fp-hold-item" class="fp-queue-item fp-queue-item--hold" data-empty="true" data-locked="false">
                <small>HOLD</small><i id="fp-held-orb" class="fp-queue-orb fp-queue-orb--held"><b></b></i><em id="fp-held-kind">EMPTY</em>
              </span>
              <span class="fp-queue-arrow" aria-hidden="true">·</span>
              <span class="fp-queue-item fp-queue-item--current"><small>NOW</small><i id="fp-current-orb" class="fp-queue-orb"><b></b></i><em id="fp-current-kind">NORMAL</em></span>
              <span class="fp-queue-arrow" aria-hidden="true">›</span>
              <span class="fp-queue-item fp-queue-item--next"><small>NEXT</small><i id="fp-next-orb" class="fp-queue-orb fp-queue-orb--next"><b></b></i><em id="fp-next-kind">NORMAL</em></span>
            </div>
            <div id="fp-combo-meter" class="fp-combo-meter" data-active="false" aria-label="현재 콤보 0, 최고 콤보 0">
              <small>COMBO</small><strong>×<b id="fp-combo">0</b></strong><span>BEST <b id="fp-best-combo">0</b></span>
            </div>
          </section>

          <section class="fp-team fp-team--orange">
            <div class="fp-team__meta fp-team__meta--right">
              <span>CORAL TEAM <i aria-hidden="true">◇</i></span><small id="fp-wins-orange">0 ROUND</small>
              <div id="fp-roster-orange" class="fp-roster fp-roster--right"></div>
            </div>
            <div class="fp-team__score" data-perspective="opponent" aria-label="상대 점수, 코랄 팀 0점"><span id="fp-score-orange-role" class="fp-team__score-role">상대 점수</span><strong id="fp-score-orange">0</strong></div>
          </section>
        </header>

        <section class="fp-play-surface" aria-label="Foam Pop 플레이필드">
          <div id="fp-render-host" class="fp-render-host" tabindex="0" role="application" aria-label="${portrait ? "내 보드를 크게 보고 위쪽 AI 보드와 비교하며 드래그하여 조준하고 놓아서 발사합니다" : duelComparison ? "왼쪽 내 보드와 오른쪽 AI 보드를 비교하며 드래그하여 조준하고 놓아서 발사합니다" : "드래그하여 조준하고 놓아서 발사합니다"}"></div>
          <div class="fp-drag-hint" aria-hidden="true"><i></i> DRAG · BANK · RELEASE</div>
          <div id="fp-pressure-chip" class="fp-pressure-chip" hidden>
            <span>PENDING</span><strong id="fp-pressure-rows">0R</strong><b id="fp-pressure-bubbles">0B</b>
          </div>
          <div id="fp-launch-warning" class="fp-launch-warning" hidden role="status" aria-live="assertive">
            <small>LAUNCH LINE</small><strong>출발선 접근</strong><span id="fp-launch-rows">3줄 남음</span>
          </div>
          <div id="fp-round-notice" class="fp-round-notice" aria-live="assertive"></div>
          ${this.touchControls()}
        </section>

        <footer class="fp-match-utility">
          <div id="fp-connection" class="fp-connection" data-state="${connection}"><i class="fp-signal"></i><span>${connection === "local" ? "LOCAL SIM" : "CONNECTING"}</span></div>
          <p class="fp-control-hint"><kbd>←</kbd><kbd>→</kbd> 조준 · <kbd>SPACE</kbd> 발사 · <kbd>C</kbd>/<kbd>SHIFT</kbd> 홀드</p>
          <div id="fp-event-toast" class="fp-event-toast" aria-live="polite"></div>
          <div class="fp-utility-actions">
            <button id="fp-music-toggle" class="fp-utility-button fp-music-toggle fp-music-toggle--compact" type="button" aria-pressed="false">
              <span aria-hidden="true">♪</span><b data-music-label>음악 시작</b><small data-music-track>PLAY</small>
            </button>
            <button id="fp-reset-match" class="fp-utility-button" type="button" hidden>재시작</button>
            <button id="fp-exit-match" class="fp-utility-button" type="button">나가기</button>
          </div>
        </footer>
        <p id="fp-a11y-status" class="fp-visually-hidden" aria-live="polite"></p>
      </main>
      ${this.orientationGuard()}
    `;
    this.bindMusicControl();
    this.required<HTMLButtonElement>("#fp-exit-match").addEventListener("click", () => {
      const onExit = this.remoteOptions?.onExit;
      this.showLanding();
      onExit?.();
    });
  }

  private attachGameAdapters(playerId: string, portrait = false, duelComparison = false): void {
    this.positionMatchHud(portrait);
    const renderHost = this.required<HTMLElement>("#fp-render-host");
    this.input.attachAimSurface(renderHost);
    this.input.attachControls(this.required<HTMLElement>("#fp-touch-controls"));
    this.input.setEnabled(true);
    this.renderer = new PuzzleRenderer({
      parent: renderHost,
      playerId,
      readAim: () => this.input.aim(),
      portrait,
      duelComparison
    });
    this.input.setAimGeometry(this.renderer.aimGeometry);
    requestAnimationFrame(() => renderHost.focus({ preventScroll: true }));
  }

  private updateHud(view: PuzzleViewModel): void {
    const primary = view.boards.find((board) => board.playerId === view.primaryPlayerId) ?? view.boards[0];
    const primaryTeam = primary?.team ?? "blue";
    const ownLabel = view.mode === "2v2" ? "우리 팀" : "내 점수";
    const opponentLabel = view.mode === "2v2" ? "상대 팀" : "상대 점수";
    this.setText("#fp-score-blue", String(view.scores.blue));
    this.setText("#fp-score-orange", String(view.scores.orange));
    const blueIsSelf = primaryTeam === "blue";
    const blueLabel = blueIsSelf ? ownLabel : opponentLabel;
    const orangeLabel = blueIsSelf ? opponentLabel : ownLabel;
    this.root.dataset.matchMode = view.mode;
    this.root.querySelector<HTMLElement>(".fp-team--blue")?.setAttribute("data-perspective", blueIsSelf ? "self" : "opponent");
    this.root.querySelector<HTMLElement>(".fp-team--orange")?.setAttribute("data-perspective", blueIsSelf ? "opponent" : "self");
    this.setText("#fp-score-blue-role", blueLabel);
    this.setText("#fp-score-orange-role", orangeLabel);
    const blueScore = this.root.querySelector<HTMLElement>(".fp-team--blue .fp-team__score");
    const orangeScore = this.root.querySelector<HTMLElement>(".fp-team--orange .fp-team__score");
    blueScore?.setAttribute("data-perspective", blueIsSelf ? "self" : "opponent");
    orangeScore?.setAttribute("data-perspective", blueIsSelf ? "opponent" : "self");
    blueScore?.setAttribute("aria-label", `${blueLabel}, 시안 팀 ${view.scores.blue}점`);
    orangeScore?.setAttribute("aria-label", `${orangeLabel}, 코랄 팀 ${view.scores.orange}점`);
    this.setText("#fp-wins-blue", `${view.roundWins.blue} ROUND`);
    this.setText("#fp-wins-orange", `${view.roundWins.orange} ROUND`);
    this.setText("#fp-round-time", view.overtime ? `OT ${Math.floor(view.overtimeTicks / PUZZLE_TICK_RATE)}` : String(Math.ceil(view.roundTimeSeconds)));
    const waitingHumans = this.remoteOptions
      ? view.players.filter((player) => player.kind === "human" && !player.connected).length
      : 0;
    this.setText(
      "#fp-round-label",
      waitingHumans > 0 && view.phase === "countdown"
        ? `WAITING · ${waitingHumans} PLAYER${waitingHumans === 1 ? "" : "S"}`
        : `ROUND ${view.round} · ${view.phase.replace("_", " ").toUpperCase()}`,
    );
    this.updateRoster("#fp-roster-blue", view.players.filter((player) => player.team === "blue"));
    this.updateRoster("#fp-roster-orange", view.players.filter((player) => player.team === "orange"));

    if (primary) {
      this.updateOrb("#fp-current-orb", "#fp-current-kind", primary.currentColor, primary.currentKind);
      this.updateOrb("#fp-next-orb", "#fp-next-kind", primary.nextColor, primary.nextKind);
      const holdItem = this.root.querySelector<HTMLElement>("#fp-hold-item");
      if (primary.heldBubble) {
        this.updateOrb("#fp-held-orb", "#fp-held-kind", primary.heldBubble.color, primary.heldBubble.kind);
        if (holdItem) holdItem.dataset.empty = "false";
      } else {
        this.clearHeldOrb();
        if (holdItem) holdItem.dataset.empty = "true";
      }
      if (holdItem) holdItem.dataset.locked = String(primary.holdLocked);
      const holdButton = this.root.querySelector<HTMLButtonElement>("[data-puzzle-action='hold']");
      if (holdButton) {
        holdButton.disabled = primary.activeShot !== null || primary.holdLocked;
        holdButton.setAttribute("aria-pressed", "false");
        holdButton.setAttribute(
          "aria-label",
          primary.holdLocked
            ? "홀드 사용 완료, 다음 발사 후 다시 사용 가능"
            : primary.activeShot
              ? "구슬 이동 중에는 홀드할 수 없음"
              : primary.heldBubble
                ? `현재 구슬과 ${primary.heldBubble.color} 홀드 구슬 교환`
                : "현재 구슬을 홀드 슬롯에 보관"
        );
      }
      this.setText("#fp-combo", String(primary.combo));
      this.setText("#fp-best-combo", String(primary.bestCombo));
      const comboMeter = this.root.querySelector<HTMLElement>("#fp-combo-meter");
      if (comboMeter) {
        comboMeter.dataset.active = String(primary.combo > 0);
        comboMeter.setAttribute("aria-label", `현재 콤보 ${primary.combo}, 최고 콤보 ${primary.bestCombo}`);
      }
      const pressure = this.root.querySelector<HTMLElement>("#fp-pressure-chip");
      if (pressure) {
        pressure.hidden = primary.pendingPressureRows <= 0 && primary.pendingAttackBubbles <= 0;
        this.setText("#fp-pressure-rows", `${primary.pendingPressureRows}R`);
        this.setText("#fp-pressure-bubbles", `${primary.pendingAttackBubbles}B`);
        pressure.setAttribute(
          "aria-label",
          `대기 압력 ${primary.pendingPressureRows}행, 공격 구슬 ${primary.pendingAttackBubbles}개`
        );
      }
      const rowsToLaunchLine = puzzleRowsBeforeLaunchLine(primary, view.logicalBoard);
      const launchWarning = this.root.querySelector<HTMLElement>("#fp-launch-warning");
      const launchDanger = Number.isFinite(rowsToLaunchLine)
        && rowsToLaunchLine <= FOAM_MUSIC_DANGER_ENTER_ROWS
        && primary.status === "active"
        && view.phase === "playing";
      if (launchWarning) {
        launchWarning.hidden = !launchDanger;
        launchWarning.dataset.critical = String(rowsToLaunchLine <= 1);
        launchWarning.setAttribute(
          "aria-label",
          `출발선까지 ${rowsToLaunchLine}줄 남았습니다. 구슬이 출발선에 닿으면 패배합니다.`
        );
        this.setText("#fp-launch-rows", rowsToLaunchLine <= 0 ? "즉시 위험" : `${rowsToLaunchLine}줄 남음`);
      }
      this.root.querySelector<HTMLElement>(".fp-play-surface")
        ?.setAttribute("data-launch-danger", String(launchDanger));
    }

    if (view.round !== this.lastRound || view.phase !== this.lastPhase) {
      const previousPhase = this.lastPhase;
      this.lastRound = view.round;
      this.lastPhase = view.phase;
      if (view.phase === "countdown") this.showRoundNotice(`ROUND ${view.round}`, "neutral");
      else if (view.phase === "round_end" && previousPhase !== "round_end") {
        this.showResultNotice(puzzleResultPresentation(view, this.playerId, "round"));
      } else if (view.phase === "match_end" && previousPhase !== "match_end") {
        this.showResultNotice(puzzleResultPresentation(view, this.playerId, "match"), true);
      }
    }

    const a11y = this.root.querySelector<HTMLElement>("#fp-a11y-status");
    if (a11y && view.tick % PUZZLE_TICK_RATE === 0) {
      const rowsToLaunchLine = primary ? puzzleRowsBeforeLaunchLine(primary, view.logicalBoard) : Number.POSITIVE_INFINITY;
      const launchStatus = Number.isFinite(rowsToLaunchLine) ? `, 출발선까지 ${rowsToLaunchLine}줄` : ", 출발선 안전";
      const opponentTeam: PuzzleTeamView = primaryTeam === "blue" ? "orange" : "blue";
      const relativeScore = `${ownLabel} ${view.scores[primaryTeam]}, ${opponentLabel} ${view.scores[opponentTeam]}`;
      a11y.textContent = `라운드 ${view.round}, ${Math.ceil(view.roundTimeSeconds)}초, ${relativeScore}${primary ? `, 콤보 ${primary.combo}, 홀드 ${primary.heldBubble ? `${primary.heldBubble.color} ${puzzleBubbleKindLabel(primary.heldBubble.kind)}` : "비어 있음"}, 대기 압력 ${primary.pendingPressureRows}행 ${primary.pendingAttackBubbles}구슬${launchStatus}` : ""}`;
    }
  }

  private updateRoster(selector: string, players: PuzzlePlayerView[]): void {
    const roster = this.root.querySelector<HTMLElement>(selector);
    if (!roster) return;
    roster.replaceChildren(...players.map((player) => {
      const item = document.createElement("span");
      item.className = `fp-roster-player${player.id === this.playerId ? " is-you" : ""}${player.connected ? "" : " is-offline"}`;
      const marker = player.team === "blue" ? "△" : "◇";
      item.textContent = `${marker} ${player.name}${player.kind === "bot" ? " · AI" : ""}`;
      return item;
    }));
  }

  private updateOrb(
    selector: string,
    kindSelector: string,
    color: string,
    kind: PuzzleBubbleKindView
  ): void {
    const orb = this.root.querySelector<HTMLElement>(selector);
    if (!orb) return;
    const style = puzzleColorStyle(color);
    orb.style.setProperty("--fp-orb-color", style.css);
    orb.style.setProperty("--fp-orb-dark", `#${style.dark.toString(16).padStart(6, "0")}`);
    orb.dataset.color = color.toLowerCase();
    orb.dataset.shape = style.shape;
    orb.dataset.kind = kind;
    orb.setAttribute("aria-label", `${color} ${puzzleBubbleKindLabel(kind)} 폼 오브`);
    this.setText(kindSelector, puzzleBubbleKindLabel(kind));
  }

  private clearHeldOrb(): void {
    const orb = this.root.querySelector<HTMLElement>("#fp-held-orb");
    if (!orb) return;
    orb.style.removeProperty("--fp-orb-color");
    orb.style.removeProperty("--fp-orb-dark");
    delete orb.dataset.color;
    delete orb.dataset.shape;
    delete orb.dataset.kind;
    orb.setAttribute("aria-label", "홀드 슬롯 비어 있음");
    this.setText("#fp-held-kind", "EMPTY");
  }

  private showHoldFeedback(): void {
    const slot = this.root.querySelector<HTMLElement>("#fp-hold-item");
    if (!slot) return;
    if (this.holdFeedbackTimer !== null) window.clearTimeout(this.holdFeedbackTimer);
    slot.classList.remove("is-swapping");
    requestAnimationFrame(() => slot.classList.add("is-swapping"));
    this.holdFeedbackTimer = window.setTimeout(() => {
      slot.classList.remove("is-swapping");
      this.holdFeedbackTimer = null;
    }, 520);
    const a11y = this.root.querySelector<HTMLElement>("#fp-a11y-status");
    if (a11y) a11y.textContent = "홀드 교환 완료. 다음 발사 후 다시 교환할 수 있습니다.";
  }

  private bindMusicControl(): void {
    const button = this.root.querySelector<HTMLButtonElement>("#fp-music-toggle");
    if (!button) return;
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      this.music.toggleFromGesture();
      this.syncMusicControl(this.music.state());
    });
    this.syncMusicControl(this.music.state());
  }

  private syncMusicControl(state: FoamMusicState): void {
    const button = this.root.querySelector<HTMLButtonElement>("#fp-music-toggle");
    if (!button) return;
    const enabled = state.unlocked && !state.muted;
    const track = FOAM_POP_MUSIC_TRACKS[state.cue];
    const cueLabel = state.cue === "intro" ? "INTRO" : state.cue === "danger" ? "DANGER" : "PLAY";
    button.dataset.enabled = String(enabled);
    button.dataset.cue = state.cue;
    button.setAttribute("aria-pressed", String(enabled));
    button.setAttribute(
      "aria-label",
      enabled ? `음악 끄기, 현재 ${track.title}` : state.muted ? "음악 켜기" : "음악 시작"
    );
    const label = button.querySelector<HTMLElement>("[data-music-label]");
    if (label) label.textContent = enabled ? "음악 끄기" : state.muted ? "음악 켜기" : "음악 시작";
    const trackLabel = button.querySelector<HTMLElement>("[data-music-track]");
    if (trackLabel) trackLabel.textContent = `${cueLabel} · CC0`;
    button.title = `${track.title} — ${track.author} (CC0)`;
  }

  private showRoundNotice(message: string, tone: PuzzleTeamView | "neutral", persistent = false): void {
    const notice = this.root.querySelector<HTMLElement>("#fp-round-notice");
    if (!notice) return;
    if (this.noticeTimer !== null) window.clearTimeout(this.noticeTimer);
    const title = document.createElement("strong");
    title.textContent = message;
    notice.replaceChildren(title);
    notice.dataset.tone = tone;
    notice.dataset.outcome = "neutral";
    notice.classList.toggle("is-final", persistent);
    notice.setAttribute("aria-label", message);
    notice.classList.add("is-visible");
    if (!persistent) {
      this.noticeTimer = window.setTimeout(() => {
        notice.classList.remove("is-visible");
        this.noticeTimer = null;
      }, 1050);
    }
  }

  private showResultNotice(result: PuzzleResultPresentation, persistent = false): void {
    const notice = this.root.querySelector<HTMLElement>("#fp-round-notice");
    if (!notice) return;
    if (this.noticeTimer !== null) window.clearTimeout(this.noticeTimer);
    const kicker = document.createElement("small");
    kicker.textContent = result.kicker;
    const title = document.createElement("strong");
    title.textContent = result.title;
    const detail = document.createElement("span");
    detail.textContent = result.detail;
    notice.replaceChildren(kicker, title, detail);
    notice.dataset.tone = result.tone;
    notice.dataset.outcome = result.outcome;
    notice.classList.toggle("is-final", persistent);
    notice.setAttribute("aria-label", result.ariaLabel);
    notice.classList.add("is-visible");
    if (!persistent) {
      this.noticeTimer = window.setTimeout(() => {
        notice.classList.remove("is-visible");
        this.noticeTimer = null;
      }, 1800);
    }
  }

  private queueEventToast(label: string, tone: string): void {
    const previous = this.toastQueue.at(-1);
    if (!previous || previous.label !== label) this.toastQueue.push({ label, tone });
    if (this.toastQueue.length > 5) this.toastQueue.splice(0, this.toastQueue.length - 5);
    if (!this.toastActive) this.showNextEventToast();
  }

  private showNextEventToast(): void {
    const next = this.toastQueue.shift();
    if (!next) {
      this.toastActive = false;
      return;
    }
    const toast = this.root.querySelector<HTMLElement>("#fp-event-toast");
    if (!toast) {
      this.toastActive = false;
      return;
    }
    this.toastActive = true;
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    toast.textContent = next.label;
    toast.dataset.tone = next.tone;
    toast.classList.remove("is-visible");
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    this.toastTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
      this.toastTimer = null;
      this.toastActive = false;
      window.setTimeout(() => this.showNextEventToast(), 35);
    }, 780);
  }

  private touchControls(): string {
    return `
      <div id="fp-touch-controls" class="fp-touch-controls" aria-label="터치 조작">
        <div class="fp-touch-aim">
          <button type="button" data-puzzle-action="left" aria-label="조준을 왼쪽으로" aria-pressed="false"><span>AIM</span>↶</button>
          <button type="button" data-puzzle-action="right" aria-label="조준을 오른쪽으로" aria-pressed="false"><span>AIM</span>↷</button>
        </div>
        <div class="fp-touch-actions">
          <button class="fp-touch-hold" type="button" data-puzzle-action="hold" aria-label="현재 구슬 홀드" aria-pressed="false"><span>HOLD</span><i aria-hidden="true">↕</i></button>
          <button class="fp-touch-fire" type="button" data-puzzle-action="fire" aria-label="폼 오브 발사" aria-pressed="false"><span>FIRE</span><i aria-hidden="true">●</i></button>
        </div>
      </div>
    `;
  }

  private orientationGuard(): string {
    return `
      <aside class="fp-orientation-guard" role="status">
        <div class="fp-rotate-device" aria-hidden="true"><span></span></div>
        <strong>가로 화면으로 돌려주세요</strong>
        <p>세로 화면에서는 보드와 조준 각도를 정확히 읽기 어렵습니다.</p>
      </aside>
    `;
  }

  private startRemoteInputLoop(): void {
    if (!this.remoteOptions || this.remoteInputTimer !== null || document.hidden) return;
    this.remoteInputTimer = window.setInterval(() => {
      try {
        this.remoteOptions?.onInput(this.input.read());
      } catch {
        this.setConnection("error", "INPUT ERROR");
      }
    }, 1000 / PUZZLE_TICK_RATE);
  }

  private stopRemoteInputLoop(): void {
    if (this.remoteInputTimer !== null) window.clearInterval(this.remoteInputTimer);
    this.remoteInputTimer = null;
  }

  private cleanupActiveMatch(): void {
    this.closeRoomCreator();
    this.input.setEnabled(false);
    this.renderer?.destroy();
    this.renderer = null;
    this.localDriver?.destroy();
    this.localDriver = null;
    this.stopRemoteInputLoop();
    this.remoteOptions = null;
    this.localMode = null;
    this.activePortrait = false;
    this.latestState = null;
    this.latestLobby = null;
    this.latestView = null;
    this.remoteConnectionState = "connecting";
    this.remoteConnectionLabel = "CONNECTING";
    this.lastPhase = "";
    this.lastRound = 0;
    if (this.noticeTimer !== null) window.clearTimeout(this.noticeTimer);
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    if (this.holdFeedbackTimer !== null) window.clearTimeout(this.holdFeedbackTimer);
    if (this.resizeFrame !== null) window.cancelAnimationFrame(this.resizeFrame);
    this.noticeTimer = null;
    this.toastTimer = null;
    this.holdFeedbackTimer = null;
    this.resizeFrame = null;
    this.toastActive = false;
    this.toastQueue = [];
  }

  private positionMatchHud(portrait: boolean): void {
    const hud = this.root.querySelector<HTMLElement>(".fp-match-hud");
    const shell = this.root.querySelector<HTMLElement>(".fp-game-shell");
    const surface = this.root.querySelector<HTMLElement>(".fp-play-surface");
    if (!hud || !shell || !surface) return;
    if (portrait) surface.append(hud);
    else shell.prepend(hud);
  }

  private applyRenderLayout(portrait: boolean): void {
    const renderHost = this.root.querySelector<HTMLElement>("#fp-render-host");
    if (!renderHost) return;
    this.activePortrait = portrait;
    this.root.classList.toggle("foam-pop--portrait", portrait);
    const duelComparison = this.localMode === "duel" && !portrait;
    this.root.classList.toggle("foam-pop--duel-compare", duelComparison);
    this.positionMatchHud(portrait);
    renderHost.setAttribute(
      "aria-label",
      portrait
        ? "왼쪽 아래 내 보드와 오른쪽 위 상대 보드를 비교하며 드래그하여 조준하고 놓아서 발사합니다"
        : "왼쪽 내 보드와 오른쪽 AI 보드를 비교하며 드래그하여 조준하고 놓아서 발사합니다"
    );
    this.renderer?.destroy();
    this.renderer = new PuzzleRenderer({
      parent: renderHost,
      playerId: this.playerId,
      readAim: () => this.input.aim(),
      portrait,
      duelComparison
    });
    this.input.setAimGeometry(this.renderer.aimGeometry);
    if (this.latestState) this.renderer.publish(this.latestState);
  }

  private shouldUsePortraitLayout(): boolean {
    return window.matchMedia?.("(orientation: portrait)").matches
      ?? window.innerHeight > window.innerWidth;
  }

  private selectedDifficulty(): PuzzleBotDifficulty {
    const selected = this.root.querySelector<HTMLInputElement>("input[name='fp-ai-difficulty']:checked")?.value;
    if (
      selected === "easy"
      || selected === "medium"
      || selected === "hard"
      || selected === "superhuman"
      || selected === "god"
    ) return selected;
    return "medium";
  }

  private difficultyOption(
    value: PuzzleBotDifficulty,
    label: string,
    note: string,
    checked = false
  ): string {
    return `
      <label>
        <input type="radio" name="fp-ai-difficulty" value="${value}"${checked ? " checked" : ""} />
        <span><strong>${label}</strong><small>${note}</small></span>
      </label>
    `;
  }

  private difficultyLabel(difficulty: PuzzleBotDifficulty): string {
    if (difficulty === "easy") return "AI 하";
    if (difficulty === "hard") return "AI 상";
    if (difficulty === "superhuman") return "AI 초인";
    if (difficulty === "god") return "AI 신";
    return "AI 중";
  }

  private async prepareStart(): Promise<void> {
    const fullscreen = this.root.querySelector<HTMLInputElement>("#fp-fullscreen-start")?.checked ?? false;
    if (!fullscreen || !document.fullscreenEnabled) return;
    await document.documentElement.requestFullscreen({ navigationUI: "hide" }).catch(() => undefined);
  }

  private teamLabel(team: PuzzleTeamView | null): string {
    return team === "blue" ? "CYAN △" : team === "orange" ? "CORAL ◇" : "DRAW";
  }

  private setText(selector: string, value: string): void {
    const node = this.root.querySelector<HTMLElement>(selector);
    if (node) node.textContent = value;
  }

  private setTextWithin(parent: ParentNode, selector: string, value: string): void {
    const node = parent.querySelector<HTMLElement>(selector);
    if (node) node.textContent = value;
  }

  private required<T extends Element>(selector: string): T {
    const node = this.root.querySelector<T>(selector);
    if (!node) throw new Error(`Foam Pop UI element missing: ${selector}`);
    return node;
  }

  private requiredWithin<T extends Element>(parent: ParentNode, selector: string): T {
    const node = parent.querySelector<T>(selector);
    if (!node) throw new Error(`Foam Pop UI element missing: ${selector}`);
    return node;
  }

  private assertAlive(): void {
    if (this.destroyed) throw new Error("PuzzleFoamApp has already been destroyed");
  }

  private escapeAttribute(value: string): string {
    return value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
  }

  private escapeHtml(value: string): string {
    return this.escapeAttribute(value).replaceAll(">", "&gt;");
  }
}
